import "./NumberPortingTile.scss";

import { useTranslation } from "react-i18next";
import { DeviceFC } from "types";

import { Section } from "@/components/Onboarding/Onboarding.constants";
import { Steps } from "@/components/Onboarding/Wizard/Steps";
import { DeviceTile } from "@/components/Portal/Cockpit/Tiles/DeviceTile/DeviceTile";
import { useGetTitle } from "@/hooks/useGetTitle";
import { getIconForDevice } from "@/utils/deviceUtils";

export const NumberPortingTile: DeviceFC = ({ device, missingInfo }) => {
  const { t } = useTranslation("portal");

  const icon = getIconForDevice(device.device?.description);
  const title = useGetTitle(device.alias, device.serviceID);

  const numberPorting = device.numberPorting;

  const verifiedOrPendingSection: Section = {
    title:
      // orange state can only occur AFTER id was verified
      numberPorting?.identificationCheckStatus === "SUCCESS" ||
      device.simStatus === "orange"
        ? "layout.tiles.numberPorting.sections.verified"
        : "layout.tiles.numberPorting.sections.pending",
    substeps: { 1: 0.001 },
  };
  const requestedSection: Section = {
    title: "layout.tiles.numberPorting.sections.requested",
    substeps: { 2: 0.001 },
  };
  const dateKnownSection: Section = {
    title: numberPorting?.date
      ? t("layout.tiles.numberPorting.sections.known", {
          val: new Date(numberPorting.date),
        })
      : "layout.tiles.numberPorting.sections.dateBeingIdentified",
    substeps: { 3: 0.001 },
  };
  const doneSection: Section = {
    title: "layout.tiles.numberPorting.sections.done",
    substeps: { 4: 0.001 },
  };

  let sections: Section[] = [
    verifiedOrPendingSection,
    requestedSection,
    dateKnownSection,
    doneSection,
  ];

  // for status orange
  if (missingInfo) {
    const missingInformationSection: Section = {
      title: "layout.tiles.numberPorting.sections.missingInformation",
      substeps: { 2: 0.001 },
    };
    const simActivationPendingSection: Section = {
      title: "layout.tiles.numberPorting.sections.simActivationPending",
      substeps: { 3: 0.001 },
    };

    sections = [
      verifiedOrPendingSection,
      missingInformationSection,
      simActivationPendingSection,
    ];
  }

  const step =
    ["pending", "requested", "dateKnown"].indexOf(numberPorting?.status || "") +
    1;

  return (
    <DeviceTile
      navigationTarget={
        missingInfo
          ? `../manage/number-porting?simId=${device.simId}`
          : `../manage?simId=${device.simId}`
      }
      dataTestid="number-porting-tile"
      icon={icon}
      buttonText={missingInfo ? t("layout.tiles.complete") : undefined}
    >
      <h2 data-testid="tile-title" className="tile-title">
        {title}
      </h2>
      <h3 className="tile-subtitle">
        {t("layout.tiles.numberPorting.numberPorting")}
      </h3>
      <div
        data-testid="number-porting-content"
        id="number-porting-content"
        className="mt-6"
      >
        <Steps
          sections={sections}
          missingInfo={missingInfo}
          // Hardcoded step for missingInfo, because it can't have another state
          currentStep={missingInfo ? 2 : step}
          currentSubStep={`${step}`}
          property="height"
        />
      </div>
    </DeviceTile>
  );
};
